/* eslint-disable import/no-duplicates */
import FullCalendar, { EventDropArg } from "@fullcalendar/react";
import { CalendarVisibleChangeEvent } from "primereact/calendar";
import bootstrapPlugin from "@fullcalendar/bootstrap";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { EventReceiveArg } from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import { addDays, format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import moment from "moment";
import { addLocale } from "primereact/api";
import {
    createRef,
    Dispatch,
    SetStateAction,
    useEffect,
    useState,
} from "react";
import { useLocation } from "react-router-dom";

import { ptBrDatepickerTranslations } from "../i18n/locales/pt-br";
import { Routes } from "../model/Enums/RoutesEnum";
import {
    getLabelTipoCartaoEnum,
    getValueTipoCartaoEnum,
    isCartaoEspecial,
    isOvertimeCard,
    TipoCartaoEnum,
} from "../model/Enums/TipoCartaoEnum";
import {
    StatusCheckinEnum,
    StatusCheckinEnumLabel,
} from "../model/Enums/StatusCheckinEnum";
import ButtonStyles from "../styles/components/ButtonStyles";
import { StyledCalendar } from "../styles/components/FullCalendarStyles";
import {
    StyledDateButton,
    StyledTitleContainer,
} from "../styles/pages/Schedule/ScheduleStyle";
import { MASK_DATE, promptMessage } from "../utils/constants";
import StringUtils from "../utils/types/StringUtils";
import { DeleteIcon, ReplyIcon } from "./Icons";
import { StyleWrapperSchedule } from "../styles/pages/Schedule/ScheduleClassesStyle";
import Legend from "../pages/Schedule/Legend/Legend";
import StatusCheckinIcon from "./StatusCheckinIcon";
import { isAvailableIcon, getEnumByLabel } from "../utils/StatusCheckinUtils";

addLocale("pt-BR", ptBrDatepickerTranslations);

const { BtnLink } = ButtonStyles;

interface CalendarProps {
    dataSchedule: any;
    holidays: any;
    viewType: string;
    onDate: (e: string) => void;
    setViewType: Dispatch<SetStateAction<string>>;
    setInitialDate: Dispatch<SetStateAction<string>>;
    setFinalDate: Dispatch<SetStateAction<string>>;
    viewDetails: (scheduleId: string, event: any) => void | Promise<void>;
    clean?: number; // incrementar sempre que precisar limpar a lista de eventos
    onDrop?: (
        cardId: string,
        date?: Date,
        event?: "drop" | "receive"
    ) => Promise<boolean>;
    disabledSchedule?: boolean;
    onRemoveIntrajornada?: (event: any) => void;
    onDeallocateCard?: (event: any) => void;
    blockActivy: boolean;
    hide?: boolean;
    handleDisableSave: (value: boolean) => void;
}

const wekeendNames = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SAB"];
const monthWekeendNames = ["SEG", "TER", "QUA", "QUI", "SEX", "SAB", "DOM"];
const wekeendFullNames = [
    "DOMINGO",
    "SEGUNDA-FEIRA",
    "TERÇA-FEIRA",
    "QUARTA-FEIRA",
    "QUINTA-FEIRA",
    "SEXTA-FEIRA",
    "SÁBADO",
];

function Schedule({
    dataSchedule = [],
    holidays = [],
    viewType,
    setViewType,
    setInitialDate,
    setFinalDate,
    viewDetails,
    clean,
    onDrop,
    onDate,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    disabledSchedule,
    onRemoveIntrajornada,
    onDeallocateCard,
    blockActivy,
    hide = false,
    handleDisableSave,
}: CalendarProps) {
    const location = useLocation();

    const pathLocation = location.pathname.split("/");

    const urlSearchParams = new URLSearchParams(window.location.search);
    const dateParam = urlSearchParams.get("date");

    const [period, setPeriod] = useState<any>(
        dateParam ? new Date(dateParam) : new Date()
    );
    const [atualizandoPeriodo, setAtualizandoPeriodo] = useState(false);
    const [calendarComponentRef] = useState<any>(createRef());
    const [events, setEvents] = useState<any[]>([]);
    const [isVisible, setIsVisible] = useState(false);

    const [dateFormat, setDateFormat] = useState("MMMM/yyyy");
    const [dateFormatValue, setDateFormatValue] = useState("");
    const [openModalLegendCards, setOpenModalLegendCards] = useState(false);
    const user1 = JSON.parse(localStorage.getItem("user") ?? "{}");

    const configurarDate = (periodRef: any) => {
        if (!atualizandoPeriodo) {
            setAtualizandoPeriodo(true);
            setTimeout(() => {
                // TODO: Verificar se o código abaixo resolveu o erro Cannot read properties of null (reading 'getApi')
                try {
                    const calendarApi = calendarComponentRef.current?.getApi();
                    if (calendarApi) {
                        calendarApi.gotoDate(periodRef);
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(error);
                } finally {
                    setTimeout(() => {
                        setAtualizandoPeriodo(false);
                    }, 100);
                }
            }, 500);
        }
    };

    useEffect(() => {
        if (calendarComponentRef.current) {
            const calendarApi = calendarComponentRef.current.getApi();
            calendarApi.removeAllEvents();
        }
    }, [clean]);

    useEffect(() => {
        const newEvents = [...dataSchedule];
        holidays?.map((data: any) =>
            newEvents.push({
                key: data.id,
                title: data.title,
                start: data.start,
                end: data.end,
                id: data.id,
                editable: false,
                color: data.color,
            })
        );
        setEvents(newEvents);
    }, [dataSchedule, holidays]);

    useEffect(() => {
        configurarDate(period);
        onDate(period);
    }, [period]);

    const handleDataSet = (event: any) => {
        const initialDateSubstring = event.startStr.substring(0, 10);
        const finalDateSubstring = event.endStr.substring(0, 10);

        setInitialDate(initialDateSubstring);
        setFinalDate(
            format(addDays(parseISO(finalDateSubstring), -1), MASK_DATE)
        );

        if (event.view.type === "dayGridMonth") {
            let month = new Date(event.endStr).getMonth();
            let year = new Date(event.endStr).getFullYear();
            year = month === 0 ? year - 1 : year;
            month = month === 0 ? 12 : month;

            const newDate = new Date(`${year}-${month}-15`);
            const date = format(newDate, dateFormat, { locale: ptBR });
            setDateFormatValue(date);
            setPeriod(newDate);
            return;
        }

        const dataInicio = new Date(
            format(addDays(parseISO(initialDateSubstring), +1), MASK_DATE)
        );

        const date = format(dataInicio, dateFormat, { locale: ptBR });

        setDateFormatValue(date);
        setPeriod(event.start);
    };

    const handleChangeDate = (event: any) => {
        const date = format(event.value, dateFormat, { locale: ptBR });
        setDateFormatValue(date);
        setPeriod(new Date(event.value));
        setIsVisible(false);
    };

    const formatDate = "YYYY-MM-DD HH:mm";

    const hasToRenderDeallocateButton = (event: any) => {
        const card = event?.event?.extendedProps;

        if (!card) return false;

        if (
            card.statusCheckin !==
            StatusCheckinEnumLabel.get(StatusCheckinEnum.AguardandoCheckin)
        ) {
            return false;
        }
        const isDacOrDocente =
            user1.isDac || card.docenteId === user1.idUsuario;

        const currentCardIsCartaoEspecial = isCartaoEspecial(
            card.cartaoTipo?.cartaoTipoId
        );
        const isInThePast = moment(
            `${card.data} ${card.horaInicio}`,
            formatDate
        ).isAfter(new Date());
        const currentCardIsOvertimeCard = isOvertimeCard(card);

        return currentCardIsOvertimeCard
            ? isDacOrDocente
            : isDacOrDocente &&
                  currentCardIsCartaoEspecial &&
                  isInThePast &&
                  pathLocation[1] !== Routes.ConsultarAgenda;
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const handleEvent = (event: any) => {
        setViewType(event.view.type);

        const card = event?.event?.extendedProps;

        const title = events.find(
            (item) => item.id && item.id === event.event.id
        )?.title;

        const eventDate = card?.data;

        if (title && event.event.title !== title) {
            event.event.setProp("title", title);
        }

        const inititalHour = `0${event.event.start.getHours()}`.slice(-2);
        const inititalMinutes = `0${event.event.start.getMinutes()}`.slice(-2);

        const finalHour = `0${event.event?.end?.getHours()}`.slice(-2);
        const finalMinutes = `0${event.event?.end?.getMinutes()}`.slice(-2);

        const initial = `${inititalHour}h${inititalMinutes}`;
        const final = `${finalHour}h${finalMinutes}`;

        const isCheckinNotDoneIcon =
            ![
                StatusCheckinEnumLabel.get(StatusCheckinEnum.CheckinRealizado),
                StatusCheckinEnumLabel.get(StatusCheckinEnum.AbonoLegal),
                StatusCheckinEnumLabel.get(StatusCheckinEnum.Dispensado),
                StatusCheckinEnumLabel.get(StatusCheckinEnum.Inativo),
            ].includes(card?.statusCheckin) &&
            getValueTipoCartaoEnum(event.event.title) !==
                TipoCartaoEnum.Intrajornada &&
            moment(`${eventDate} ${card?.horaInicio}`, formatDate).isBefore(
                new Date()
            );

        const isValidCard = () => {
            return !!Object.keys(card).length;
        };

        const doNotRenderIcon = () => {
            if (!isValidCard()) {
                return true;
            }

            return [TipoCartaoEnum.Intrajornada].includes(
                card?.cartaoTipo?.cartaoTipoId
            );
        };

        const checkinIcons = () => {
            if (doNotRenderIcon()) return null;

            const enumStatusCheckin = getEnumByLabel(card?.statusCheckin);

            const isFeriado =
                !!card?.data &&
                holidays.some((h: any) => {
                    const sameDate =
                        moment(h.start).format("YYYY-MM-DD") === card.data;
                    if (!sameDate) return false;
                    // National holiday applies to all campuses
                    if (h.type === "N") return true;
                    // Municipal/Recesso must match the card campus
                    const cardCampus = card?.abrangencia?.campus;
                    return (
                        (h.type === "M" || h.type === "R") &&
                        h.campus &&
                        cardCampus &&
                        h.campus === cardCampus
                    );
                });
            if (isFeriado) {
                return (
                    <StatusCheckinIcon
                        status={StatusCheckinEnum.FeriadoNacional}
                        className="svg-icon svg-icon-margin"
                    />
                );
            }

            if (isAvailableIcon(enumStatusCheckin)) {
                return (
                    <StatusCheckinIcon
                        status={enumStatusCheckin}
                        className="svg-icon svg-icon-margin"
                    />
                );
            }

            if (isCheckinNotDoneIcon) {
                return (
                    <StatusCheckinIcon
                        status={StatusCheckinEnum.PendenciaJustificativa}
                        className="svg-icon svg-icon-margin"
                    />
                );
            }

            return null;
        };

        const showIconJustInFuture =
            card &&
            moment(`${eventDate} ${card?.horaInicio}`, formatDate).isAfter(
                new Date()
            );

        const showIconTodayAndFuture = moment(
            `${eventDate} ${card?.horaInicio}`,
            formatDate
        ).isAfter(moment().startOf("day"));

        const showDeleteIcon = !blockActivy &&
            showIconTodayAndFuture &&
            pathLocation[1] !== Routes.ConsultarAgenda && (
                <DeleteIcon
                    style={{
                        fill: event.textColor,
                        paddingBottom: 4,
                    }}
                    className="svg-icon deallocate-icon-intrajornada"
                />
            );

        const today: Date = new Date();
        const dateString = today.toISOString().substring(0, 10);

        const isFeriado =
            !!eventDate &&
            holidays.some((h: any) => {
                const sameDate = h.start?.startsWith(eventDate);
                if (!sameDate) return false;
                if (h.type === "N") return true;
                const cardCampus = card?.abrangencia?.campus;
                return (
                    (h.type === "M" || h.type === "R") &&
                    h.campus &&
                    cardCampus &&
                    h.campus === cardCampus
                );
            });

        const enumStatusCheckin = getEnumByLabel("Feriado Nacional");
        // no-op; keep variable removed to satisfy linter

        const showReplyIcon =
            eventDate !== undefined &&
            !isFeriado &&
            (dateString === eventDate || showIconJustInFuture) ? (
                <ReplyIcon
                    style={{
                        fill: event.textColor,
                        paddingBottom: 4,
                    }}
                    className="svg-icon deallocate-icon"
                />
            ) : null;

        const workload = card?.duracaoHoraAula;

        function reducingText(text: string): string {
            if (workload === 30 && event.event.title.length > 14) {
                return `${text.substring(0, 14)}...`;
            }

            if (workload === 60 && event.event.title.length > 40) {
                return `${text.substring(0, 40)}...`;
            }

            return text.substring(0, workload);
        }

        const reduceText = reducingText(event.event.title);

        const eventAllDays = card?.locale ? (
            <span
                className="fc-event-time"
                style={{
                    whiteSpace: "nowrap",
                    fontSize: 10,
                    fontWeight: 400,
                    lineHeight: "100%",
                }}
            >
                Local: <strong>{card?.locale}</strong>
            </span>
        ) : (
            <div />
        );

        if (event.view.type === "dayGridMonth") {
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 5,
                        paddingTop: 1,
                        paddingBottom: 1,
                        alignItems: "center",
                    }}
                >
                    {event.event.allDay ? (
                        <span
                            style={{
                                whiteSpace: "nowrap",
                                fontSize: 12,
                                lineHeight: "100%",
                            }}
                            className="fc-event-title"
                        >
                            <strong>{event.event.title}</strong>
                        </span>
                    ) : (
                        <>
                            <div
                                style={{
                                    borderRadius: "50%",
                                    width: 8,
                                    height: 8,
                                    background: event.backgroundColor,
                                }}
                            />
                            <span className="fc-event-time">
                                <strong>{initial}</strong>
                            </span>
                            <span
                                style={{ whiteSpace: "nowrap" }}
                                className="fc-event-title"
                            >
                                <strong>{event.event.title}</strong>
                            </span>
                        </>
                    )}
                </div>
            );
        }

        const classTime =
            workload === 60 ? (
                <>
                    <p style={{ marginBottom: "5px" }}>
                        Início:<strong>{initial}</strong>
                    </p>
                    <p>
                        Fim: <strong>{final}</strong>
                    </p>
                </>
            ) : (
                <p style={{ marginBottom: "5px" }}>
                    Hora:
                    <strong>
                        {initial}-{final}
                    </strong>
                </p>
            );

        const interjourneyTime = (
            <span
                style={{
                    marginLeft: 5,
                }}
            >
                - Início: <strong>{initial}</strong>
            </span>
        );

        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                }}
            >
                <div
                    key={event?._def?.publicId}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                        paddingTop: 1,
                        paddingBottom: 1,
                    }}
                >
                    <span
                        style={{
                            fontSize: event.timeText === "" ? 12 : 11,
                            lineHeight: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                        className="fc-event-title"
                    >
                        <strong>
                            {reduceText}
                            {event.event?._def?.extendedProps?.cartaoTipo
                                ?.cartaoTipoId &&
                                event.event._def.extendedProps.cartaoTipo
                                    .cartaoTipoId ===
                                    TipoCartaoEnum.Intrajornada &&
                                interjourneyTime}
                        </strong>
                    </span>
                    {event.timeText === "" ||
                    event.event.title === "Recreio - Estudantes" ||
                    event.event.title ===
                        getLabelTipoCartaoEnum(
                            TipoCartaoEnum.Intrajornada
                        ) ? null : (
                        <>
                            <span
                                className="fc-event-time"
                                style={{
                                    whiteSpace: "nowrap",
                                    fontSize: 10,
                                    fontWeight: 400,
                                    lineHeight: "100%",
                                }}
                            >
                                {classTime}
                            </span>
                            {eventAllDays}
                        </>
                    )}
                </div>
                <div
                    style={{
                        zIndex: 999,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent:
                            event.event?._def?.extendedProps?.duracaoHoraAula &&
                            event.event?._def?.extendedProps?.duracaoHoraAula <
                                60
                                ? "start"
                                : "end",
                        alignItems: "center",
                        paddingBottom: 12,
                        position: "absolute",
                        right: 0,
                        minHeight:
                            event.event?._def?.extendedProps?.cartaoTipo
                                ?.cartaoTipoId &&
                            event.event._def.extendedProps.cartaoTipo
                                .cartaoTipoId === TipoCartaoEnum.Intrajornada
                                ? 0
                                : 76,
                    }}
                    className="icons-container"
                >
                    {hasToRenderDeallocateButton(event) && (
                        <BtnLink
                            className="deallocate-btn"
                            style={{
                                alignItems: "end",
                                paddingBottom: 0,
                                paddingRight: 2,
                                minHeight: 24,
                                maxHeight: 24,
                            }}
                        >
                            {showReplyIcon}
                        </BtnLink>
                    )}
                    {checkinIcons()}
                    {(user1.isDac || user1.isDocente) &&
                        getValueTipoCartaoEnum(event.event.title) ===
                            TipoCartaoEnum.Intrajornada && (
                            <BtnLink
                                className="deallocate-btn-intrajornada"
                                onClick={() => {
                                    if (onRemoveIntrajornada)
                                        onRemoveIntrajornada(event);
                                }}
                            >
                                {showDeleteIcon}
                            </BtnLink>
                        )}
                </div>
            </div>
        );
    };

    const handleDateFormat = (event: any) => {
        if (viewType === "dayGridMonth") {
            setDateFormat("MMMM yyyy");
            return `${wekeendNames[event.date.marker.getDay()]}`;
        }

        if (viewType === "timeGridDay") {
            setDateFormat("dd 'de' MMMM 'de' yyyy");
            return `${wekeendFullNames[event.date.marker.getDay()]}`;
        }

        setDateFormat("MMMM yyyy");
        return `${wekeendNames[event.date.marker.getDay()]} - ${
            event.date.day
        }`;
    };

    const handlePopover = (event: any) => {
        if (event?.view?.type !== "dayGridMonth") {
            return "";
        }

        const formatHour = `0${event?.date?.getDate()}`.slice(-2);
        return (
            <h1
                style={{
                    fontSize: 13,
                    lineHeight: "15.6px",
                    margin: 0,
                }}
            >
                {formatHour}
            </h1>
        );
    };

    const handleHeaderContent = (args: any) => {
        if (args.view.type === "timeGridWeek") {
            return (
                <div>
                    <StyledTitleContainer>
                        {wekeendNames[args.date.getDay()]}
                    </StyledTitleContainer>

                    {args.isToday ? (
                        <div
                            style={{
                                backgroundColor: "#F0F2F2",
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                marginLeft: 12,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <StyledTitleContainer
                                style={{
                                    fontSize: 17,
                                    lineHeight: "25.5px",
                                    color: "#787878",
                                    textAlign: "start",
                                    margin: 0,
                                }}
                            >
                                {args.date.getDate()}
                            </StyledTitleContainer>
                        </div>
                    ) : (
                        <StyledTitleContainer
                            style={{
                                fontSize: 17,
                                lineHeight: "25.5px",
                                color: "#787878",
                                textAlign: "start",
                                margin: 0,
                                marginLeft: 24,
                            }}
                        >
                            {args.date.getDate()}
                        </StyledTitleContainer>
                    )}
                </div>
            );
        }
        if (args.view.type === "dayGridMonth") {
            return (
                <StyledTitleContainer>
                    {monthWekeendNames[args.date.getDay()]}
                </StyledTitleContainer>
            );
        }

        return (
            <StyledTitleContainer>
                {wekeendFullNames[args.date.getDay()]}
            </StyledTitleContainer>
        );
    };

    const handleVisibility = (e: CalendarVisibleChangeEvent) => {
        if (e?.type === "outside") {
            setIsVisible(true);
            return;
        }

        setIsVisible(!isVisible);
    };

    const handleOpenModalLegendCards = () => {
        setOpenModalLegendCards(true);
    };

    const handleCloseModalLegendCards = () => {
        setOpenModalLegendCards(false);
    };

    const modalLegendCards = (
        <Legend
            open={openModalLegendCards}
            onClose={handleCloseModalLegendCards}
        />
    );

    // eslint-disable-next-line @typescript-eslint/ban-types
    const renderConfirmationDialog = (callback: Function) => {
        if (pathLocation[1] === Routes.ConsultarAgenda) {
            handleDisableSave(true);
            callback();
            return;
        }

        if (
            // eslint-disable-next-line no-restricted-globals, no-alert
            confirm(promptMessage) === true
        ) {
            handleDisableSave(true);
            callback();
        }
    };

    const handleEventClick = ({
        event,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        el,
        jsEvent,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        view,
    }: {
        event: any;
        el: any;
        jsEvent: any;
        view: any;
    }) => {
        if (
            jsEvent.target.parentElement.classList.contains(
                "deallocate-icon"
            ) &&
            onDeallocateCard
        ) {
            onDeallocateCard(event);
            return;
        }

        // Lógica criada para Aviso Salvar Agenda não aparecer ao clicar no icones dentro do cartão
        if (
            jsEvent.target.parentElement.classList.contains("deallocate-btn") ||
            jsEvent.target.parentElement.classList.contains(
                "deallocate-btn-intrajornada"
            ) ||
            jsEvent.target.parentElement.classList.contains(
                "deallocate-icon-intrajornada"
            )
        ) {
            return;
        }
        viewDetails(event.id, event);
    };

    const addLeadingZero = (number: number) => {
        return number < 10 ? `0${number}` : number;
    };

    const setScrollTimeInitialTime = () => {
        const dateNow = new Date();

        const hours = dateNow.getHours();
        const minutes = dateNow.getMinutes();
        const seconds = dateNow.getSeconds();

        return `${
            hours > 2 ? addLeadingZero(hours - 1) : addLeadingZero(hours)
        }:${addLeadingZero(minutes)}:${addLeadingZero(seconds)}`;
    };

    function getMonday(date: Date) {
        const now = new Date(date);
        return new Date(now.setDate(now.getDate() - now.getDay() + 1));
    }

    if (hide) return null;

    return (
        <>
            <div
                className="App"
                style={{
                    position: "relative",
                }}
            >
                <StyledDateButton>
                    <StyledCalendar
                        value={period}
                        onChange={handleChangeDate}
                        locale="pt-BR"
                        dateFormat={dateFormat}
                        yearRange="2010:3000"
                        style={{ border: "transparent" }}
                        showOnFocus={false}
                        visible={isVisible}
                        onVisibleChange={handleVisibility}
                    />
                </StyledDateButton>
                <StyleWrapperSchedule
                    style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        left: 0,
                        bottom: 0,
                    }}
                >
                    <FullCalendar
                        plugins={[
                            dayGridPlugin,
                            timeGridPlugin,
                            interactionPlugin,
                            bootstrapPlugin,
                        ]}
                        locale={ptBrLocale}
                        initialView="timeGridWeek"
                        customButtons={{
                            legend: {
                                text: "Legenda",
                                hint: "Legenda",
                                themeIcon: "standard",
                                click() {
                                    handleOpenModalLegendCards();
                                },
                                icon: "pi pi pi-info-circle",
                            },
                            legend2: {
                                text: "Legenda",
                                hint: "Legenda",
                                themeIcon: "standard",
                                click() {
                                    handleOpenModalLegendCards();
                                },
                            },
                            pickCalendar: {
                                text: StringUtils.capitalizeFirstLetter(
                                    dateFormatValue
                                ),
                                click() {
                                    renderConfirmationDialog(handleVisibility);
                                },
                            },
                            icon: {
                                text: "",
                                click() {
                                    renderConfirmationDialog(handleVisibility);
                                },
                                icon: !isVisible
                                    ? "pi pi pi-angle-down"
                                    : "pi pi pi-angle-up",
                            },
                            today: {
                                text: "Hoje",
                                click() {
                                    renderConfirmationDialog(() => {
                                        const calendarApi =
                                            calendarComponentRef.current.getApi();
                                        calendarApi.today();
                                    });
                                },
                            },
                            prev: {
                                text: "Anterior",
                                click() {
                                    renderConfirmationDialog(() => {
                                        const calendarApi =
                                            calendarComponentRef.current.getApi();
                                        calendarApi.prev();
                                    });
                                },
                            },
                            next: {
                                text: "Próximo",
                                click() {
                                    renderConfirmationDialog(() => {
                                        const calendarApi =
                                            calendarComponentRef.current.getApi();
                                        calendarApi.next();
                                    });
                                },
                            },
                            timeGridDay: {
                                text: "Dia",
                                click() {
                                    renderConfirmationDialog(() => {
                                        const calendarApi =
                                            calendarComponentRef.current.getApi();
                                        calendarApi.changeView(
                                            "timeGridDay",
                                            new Date()
                                        );
                                    });
                                },
                            },
                            timeGridWeek: {
                                text: "Semana",
                                click() {
                                    renderConfirmationDialog(() => {
                                        const calendarApi =
                                            calendarComponentRef.current.getApi();

                                        calendarApi.changeView(
                                            "timeGridWeek",
                                            getMonday(new Date())
                                        );
                                    });
                                },
                            },
                            dayGridMonth: {
                                text: "Mês",
                                click() {
                                    renderConfirmationDialog(() => {
                                        const calendarApi =
                                            calendarComponentRef.current.getApi();
                                        calendarApi.changeView("dayGridMonth");
                                    });
                                },
                            },
                        }}
                        headerToolbar={{
                            start: "today prev,next pickCalendar,icon",
                            center: "",
                            end: "legend,legend2 timeGridDay,timeGridWeek,dayGridMonth",
                        }}
                        dayHeaderFormat={handleDateFormat}
                        ref={calendarComponentRef}
                        events={events}
                        themeSystem="standard"
                        nowIndicator
                        dayHeaderContent={handleHeaderContent}
                        droppable
                        datesSet={handleDataSet}
                        eventClick={handleEventClick}
                        height="640px"
                        allDaySlot={holidays.length > 0}
                        firstDay={1}
                        editable
                        displayEventEnd
                        contentHeight="600"
                        slotEventOverlap={false}
                        eventOverlap
                        eventContent={handleEvent}
                        slotLabelFormat={(param) => {
                            const hours = `0${param.start.hour}`.slice(-2);
                            const minutes = `0${param.start.minute}`.slice(-2);
                            return `${hours}:${minutes}`;
                        }}
                        dayHeaderClassNames="fc-event-overirde"
                        eventClassNames="fc-event-overirde"
                        dayMaxEvents={2}
                        eventOrder={["-duration", "start"]}
                        snapDuration="00:01:00"
                        eventDurationEditable={false}
                        dayCellContent={handlePopover}
                        dayPopoverFormat={(param) => {
                            return monthWekeendNames[
                                param.date.marker.getDay()
                            ];
                        }}
                        eventDrop={async (e: EventDropArg) => {
                            if (onDrop) {
                                const result = await onDrop(
                                    e.event._def.publicId || "0",
                                    e.event._instance?.range.start,
                                    "drop"
                                );
                                if (!result) e.revert();
                            }
                        }}
                        eventReceive={async (e: EventReceiveArg) => {
                            if (onDrop) {
                                const result = await onDrop(
                                    e.event._def.publicId || "0",
                                    e.event._instance?.range.start,
                                    "receive"
                                );

                                if (!result) e.revert();
                            }
                        }}
                        scrollTime={setScrollTimeInitialTime()}
                    />
                </StyleWrapperSchedule>
            </div>
            {openModalLegendCards && modalLegendCards}
        </>
    );
}

export default Schedule;
